import React, { useState, useEffect } from 'react';
import {
  Card, CardBody, Button, Form, FormGroup, Input, Label, Badge,
  Modal, ModalHeader, ModalBody, ModalFooter
} from 'reactstrap';
import { MessageSquare, User, Clock, Check, Trash2, RefreshCw, AlertCircle } from 'react-feather';
import axios from 'axios';
import moment from 'moment';
import Swal from 'sweetalert2';
import { baseURL } from '../../../../Services/api/baseURL';

const BlogComments = ({ blogId, commentsEnabled = true, isAdmin = false }) => {
  const token = JSON.parse(localStorage.getItem("token"));
  
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showReplyModal, setShowReplyModal] = useState(false);
  const [replyToComment, setReplyToComment] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    website: '',
    content: ''
  });

  useEffect(() => {
    if (blogId) {
      fetchComments();
    }
  }, [blogId]);

  const fetchComments = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${baseURL}/api/blogs/${blogId}/comments`, {
        params: { includeUnapproved: isAdmin },
        headers: isAdmin ? { Authorization: `${token}` } : {}
      });
      setComments(response.data.data || []);
    } catch (error) {
      console.error('Error fetching comments:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmitComment = async (e, parentComment = null) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.content) {
      Swal.fire({
        title: "Missing Fields",
        text: "Please fill in name, email, and comment",
        icon: "warning",
        confirmButtonColor: "#fc2c54",
      });
      return;
    }

    setSubmitting(true);
    try {
      const commentData = {
        ...formData,
        parentComment: parentComment
      };

      await axios.post(`${baseURL}/api/blogs/${blogId}/comments`, commentData);

      Swal.fire({
        title: "Comment Submitted!",
        text: "Your comment will appear after approval.",
        icon: "success",
        timer: 3000,
        showConfirmButton: false
      });

      setFormData({
        name: '',
        email: '',
        website: '',
        content: ''
      });
      setShowReplyModal(false);
      setReplyToComment(null);

      // Refresh comments if admin
      if (isAdmin) {
        fetchComments();
      }
    } catch (error) {
      console.error('Error submitting comment:', error);
      Swal.fire({
        title: "Error",
        text: error.response?.data?.message || "Failed to submit comment",
        icon: "error",
        confirmButtonColor: "#fc2c54",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleApproveComment = async (commentId) => {
    try {
      await axios.patch(
        `${baseURL}/api/blogs/${blogId}/comments/${commentId}/approve`,
        {},
        {
          headers: { Authorization: `${token}` }
        }
      );

      Swal.fire({
        title: "Approved!",
        text: "Comment has been approved.",
        icon: "success",
        timer: 2000,
        showConfirmButton: false
      });

      fetchComments();
    } catch (error) {
      console.error('Error approving comment:', error);
      Swal.fire({
        title: "Error",
        text: "Failed to approve comment",
        icon: "error",
        confirmButtonColor: "#fc2c54",
      });
    }
  };

  const handleDeleteComment = async (commentId) => {
    const result = await Swal.fire({
      title: "Delete Comment?",
      text: "This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc3545",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, delete it"
    });

    if (result.isConfirmed) {
      try {
        await axios.delete(
          `${baseURL}/api/blogs/${blogId}/comments/${commentId}`,
          {
            headers: { Authorization: `${token}` }
          }
        );

        Swal.fire({
          title: "Deleted!",
          text: "Comment has been deleted.",
          icon: "success",
          timer: 2000,
          showConfirmButton: false
        });

        fetchComments();
      } catch (error) {
        console.error('Error deleting comment:', error);
        Swal.fire({
          title: "Error",
          text: "Failed to delete comment",
          icon: "error",
          confirmButtonColor: "#fc2c54",
        });
      }
    }
  };

  const openReplyModal = (comment) => {
    setReplyToComment(comment);
    setShowReplyModal(true);
  };

  // Comment component
  const CommentItem = ({ comment, isReply = false }) => (
    <div 
      className={`comment-item ${isReply ? 'ms-4 border-start ps-3' : ''}`}
      style={{ 
        padding: '16px', 
        borderBottom: isReply ? 'none' : '1px solid #e9ecef',
        backgroundColor: !comment.isApproved ? '#fff9e6' : 'transparent'
      }}
    >
      <div className="d-flex justify-content-between align-items-start mb-2">
        <div className="d-flex align-items-center gap-2">
          <div 
            style={{ 
              width: '40px', 
              height: '40px', 
              borderRadius: '50%', 
              backgroundColor: '#e9ecef',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <User size={20} color="#6c757d" />
          </div>
          <div>
            <div style={{ fontWeight: '600', color: '#2c3e50' }}>
              {comment.name}
              {comment.website && (
                <a 
                  href={comment.website} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="ms-2"
                  style={{ fontSize: '12px' }}
                >
                  (website)
                </a>
              )}
            </div>
            <div style={{ fontSize: '12px', color: '#6c757d', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Clock size={12} />
              {moment(comment.createdAt).format("MMM DD, YYYY [at] h:mm A")}
            </div>
          </div>
        </div>
        
        <div className="d-flex align-items-center gap-2">
          {!comment.isApproved && (
            <Badge color="warning" style={{ fontSize: '10px' }}>
              <AlertCircle size={10} className="me-1" />
              Pending
            </Badge>
          )}
          
          {isAdmin && (
            <div className="d-flex gap-1">
              {!comment.isApproved && (
                <Button 
                  color="success" 
                  size="sm" 
                  onClick={() => handleApproveComment(comment._id)}
                  title="Approve"
                  style={{ padding: '4px 8px' }}
                >
                  <Check size={12} />
                </Button>
              )}
              <Button 
                color="danger" 
                size="sm" 
                onClick={() => handleDeleteComment(comment._id)}
                title="Delete"
                style={{ padding: '4px 8px' }}
              >
                <Trash2 size={12} />
              </Button>
            </div>
          )}
        </div>
      </div>
      
      <div style={{ marginLeft: '52px' }}>
        <p style={{ color: '#374151', marginBottom: '12px', lineHeight: '1.6' }}>
          {comment.content}
        </p>
        
        {commentsEnabled && !isReply && (
          <Button 
            color="link" 
            size="sm" 
            onClick={() => openReplyModal(comment)}
            style={{ padding: 0, fontSize: '13px' }}
          >
            <RefreshCw size={14} className="me-1" />
            Reply
          </Button>
        )}
      </div>

      {/* Replies */}
      {comment.replies && comment.replies.length > 0 && (
        <div className="mt-3">
          {comment.replies.map(reply => (
            <CommentItem key={reply._id} comment={reply} isReply={true} />
          ))}
        </div>
      )}
    </div>
  );

  if (!commentsEnabled) {
    return (
      <Card className="shadow-sm mt-4">
        <CardBody className="text-center py-4">
          <MessageSquare size={32} color="#6c757d" className="mb-2" />
          <p className="text-muted mb-0">Comments are disabled for this post.</p>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card className="shadow-sm mt-4">
      <CardBody className="p-4">
        <h5 className="mb-4 d-flex align-items-center gap-2">
          <MessageSquare size={20} />
          Comments ({comments.length})
        </h5>

        {/* Comment Form */}
        <div className="comment-form mb-4 p-3" style={{ backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
          <h6 className="mb-3">Leave a Comment</h6>
          <Form onSubmit={(e) => handleSubmitComment(e)}>
            <div className="row">
              <div className="col-md-4">
                <FormGroup>
                  <Label size="sm">Name <span className="text-danger">*</span></Label>
                  <Input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Your name"
                    required
                    bsSize="sm"
                  />
                </FormGroup>
              </div>
              <div className="col-md-4">
                <FormGroup>
                  <Label size="sm">Email <span className="text-danger">*</span></Label>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="your@email.com"
                    required
                    bsSize="sm"
                  />
                  <small className="text-muted">Email won't be published</small>
                </FormGroup>
              </div>
              <div className="col-md-4">
                <FormGroup>
                  <Label size="sm">Website</Label>
                  <Input
                    type="url"
                    name="website"
                    value={formData.website}
                    onChange={handleInputChange}
                    placeholder="https://yoursite.com"
                    bsSize="sm"
                  />
                </FormGroup>
              </div>
            </div>
            <FormGroup>
              <Label size="sm">Comment <span className="text-danger">*</span></Label>
              <Input
                type="textarea"
                name="content"
                value={formData.content}
                onChange={handleInputChange}
                placeholder="Write your comment here..."
                rows="4"
                required
              />
            </FormGroup>
            <Button 
              color="primary" 
              type="submit" 
              disabled={submitting}
              style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
            >
              <MessageSquare size={16} />
              {submitting ? 'Posting...' : 'Post Comment'}
            </Button>
          </Form>
        </div>

        {/* Comments List */}
        {loading ? (
          <div className="text-center py-4">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : comments.length === 0 ? (
          <div className="text-center py-4">
            <MessageSquare size={40} color="#dee2e6" className="mb-2" />
            <p className="text-muted mb-0">No comments yet. Be the first to comment!</p>
          </div>
        ) : (
          <div className="comments-list">
            {comments.map(comment => (
              <CommentItem key={comment._id} comment={comment} />
            ))}
          </div>
        )}
      </CardBody>

      {/* Reply Modal */}
      <Modal isOpen={showReplyModal} toggle={() => setShowReplyModal(false)}>
        <ModalHeader toggle={() => setShowReplyModal(false)}>
          Reply to {replyToComment?.name}
        </ModalHeader>
        <ModalBody>
          {replyToComment && (
            <div 
              className="mb-3 p-2" 
              style={{ backgroundColor: '#f8f9fa', borderRadius: '4px', borderLeft: '3px solid #007bff' }}
            >
              <small className="text-muted">Replying to:</small>
              <p className="mb-0 mt-1" style={{ fontSize: '14px' }}>
                {replyToComment.content.length > 100 
                  ? replyToComment.content.substring(0, 100) + '...' 
                  : replyToComment.content}
              </p>
            </div>
          )}
          <Form onSubmit={(e) => handleSubmitComment(e, replyToComment?._id)}>
            <div className="row">
              <div className="col-md-6">
                <FormGroup>
                  <Label size="sm">Name <span className="text-danger">*</span></Label>
                  <Input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Your name"
                    required
                    bsSize="sm"
                  />
                </FormGroup>
              </div>
              <div className="col-md-6">
                <FormGroup>
                  <Label size="sm">Email <span className="text-danger">*</span></Label>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="your@email.com"
                    required
                    bsSize="sm"
                  />
                </FormGroup>
              </div>
            </div>
            <FormGroup>
              <Label size="sm">Reply <span className="text-danger">*</span></Label>
              <Input
                type="textarea"
                name="content"
                value={formData.content}
                onChange={handleInputChange}
                placeholder="Write your reply..."
                rows="4"
                required
              />
            </FormGroup>
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setShowReplyModal(false)}>
            Cancel
          </Button>
          <Button 
            color="primary" 
            onClick={(e) => handleSubmitComment(e, replyToComment?._id)}
            disabled={submitting}
          >
            {submitting ? 'Posting...' : 'Post Reply'}
          </Button>
        </ModalFooter>
      </Modal>
    </Card>
  );
};

export default BlogComments;